// Sugalarm — alarm-clock-mode controller (cycle 145).
//
// `useAlarmClockMode(...)` is the SINGLE controller, mounted once in
// App's authenticated shell. It owns:
//   - scheduled auto-enter/exit against the persisted `alarm_clock`
//     settings key (evaluated on a 15s tick + on every settings change,
//     so a `settings_update` WS broadcast flips every device live);
//   - manual-session auto-exit (the 1h/8h durations);
//   - clearing the manual-exit suppression when the window ends;
//   - the dim derivation (auto inside the window; manual override;
//     FORCED OFF while any alarm episode is live).
//
// It must NOT touch `useAlarmEpisode` / `useAlarmSounds` — it only
// reads `episodeLive` to suspend dim. No alarm surface consults clock
// mode; the face renders BELOW them all.
//
// Lighter consumers (nav buttons, frames, NowScreen) use
// `useClockMode()` — a bare store subscription with no controller
// logic — so the schedule evaluator runs exactly once.

import { useCallback, useEffect, useSyncExternalStore } from "react";

import type { AlarmClockSchedule } from "../lib/types";
import {
  clearClockSuppression,
  enterClockMode,
  exitClockMode,
  getClockModeState,
  subscribeClockMode,
  toggleClockDim,
  type ClockModeState,
} from "../lib/clockMode";
import { allowAnyOrientation, lockPortrait } from "../lib/orientationLock";

/** Is `now` inside the schedule's window? An overnight window
 *  (`end <= start`) wraps past midnight; the `days` gate applies to the
 *  day the window STARTED (a Mon 22:00→07:00 window covers Tue 03:00
 *  when Monday is enabled). */
export function isInClockWindow(
  sched: AlarmClockSchedule | undefined | null,
  now: Date,
): boolean {
  if (!sched?.enabled) return false;
  if (!Array.isArray(sched.days) || sched.days.length !== 7) return false;
  const [sh, sm] = sched.start.split(":").map(Number);
  const [eh, em] = sched.end.split(":").map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return false;
  const minutes = now.getHours() * 60 + now.getMinutes();
  const start = sh! * 60 + sm!;
  const end = eh! * 60 + em!;
  const day = now.getDay(); // 0 = Sunday, matching days[0]
  if (end > start) {
    // Same-day window.
    return sched.days[day] === true && minutes >= start && minutes < end;
  }
  // Overnight wrap: tonight's leg (gated on today) or this morning's
  // leg (gated on yesterday — the day the window started).
  if (minutes >= start) return sched.days[day] === true;
  if (minutes < end) return sched.days[(day + 6) % 7] === true;
  return false;
}

export interface ClockMode {
  active: boolean;
  /** Whole-face dim is on (auto in-window / manual override), and no
   *  alarm episode is live. */
  dim: boolean;
  state: ClockModeState;
  enter: (
    reason: "nav" | "manual",
    opts?: { durationMs?: number | null; priorPath?: string | null },
  ) => void;
  exit: (opts?: { manual?: boolean }) => void;
  toggleDim: () => void;
}

/** Bare store subscription — for nav buttons / frames / NowScreen. */
export function useClockMode(): ClockModeState {
  // Cycle 435: the server snapshot is the same module state — `useAlarmPreview`
  // already does this — so a screen can be rendered under `react-dom/server`
  // by the thaw acceptance test. Nothing changes for the client.
  return useSyncExternalStore(subscribeClockMode, getClockModeState, getClockModeState);
}

export function useAlarmClockMode(
  schedule: AlarmClockSchedule | undefined | null,
  episodeLive: boolean,
): ClockMode {
  const state = useClockMode();

  // Cycle 348: orientation ownership. The Clock face is the ONE screen
  // that's exempt from the app-wide portrait lock (App.tsx locks on native
  // mobile launch) — it renders PortraitClockFace or the landscape bedside
  // face depending on how the device is physically held, so it needs free
  // rotation for exactly as long as it's the active screen. `active` going
  // true allows any orientation; `active` going false re-locks to portrait
  // immediately, snapping the view back even if the phone is still
  // physically in landscape. Both calls no-op safely on web/kiosk/a
  // non-native browser (lib/orientationLock.ts self-gates), so this is
  // safe to run unconditionally on every variant and on every render of
  // this single, once-mounted controller — including the initial mount
  // with `active === false`, which is a harmless, idempotent re-assertion
  // of the same portrait lock App.tsx already applied on launch.
  useEffect(() => {
    if (state.active) void allowAnyOrientation();
    else void lockPortrait();
  }, [state.active]);

  // Scheduled enter/exit + manual auto-exit + suppression clearing.
  useEffect(() => {
    const tick = () => {
      const s = getClockModeState();
      const now = new Date();
      const inWindow = isInClockWindow(schedule, now);
      if (!inWindow && s.suppressedUntilWindowEnd) {
        clearClockSuppression();
      }
      if (
        inWindow &&
        !s.active &&
        !s.suppressedUntilWindowEnd
      ) {
        enterClockMode("schedule");
        return;
      }
      if (s.active && s.reason === "schedule" && !inWindow) {
        exitClockMode();
        return;
      }
      if (s.active && s.autoExitAt != null && Date.now() >= s.autoExitAt) {
        exitClockMode();
      }
    };
    tick();
    const id = window.setInterval(tick, 15_000);
    return () => window.clearInterval(id);
  }, [schedule]);

  const inWindowNow = state.active
    ? isInClockWindow(schedule, new Date())
    : false;
  const dim =
    state.active &&
    !episodeLive &&
    (state.dimOverride ?? (state.reason === "schedule" || inWindowNow));

  const enter = useCallback<ClockMode["enter"]>((reason, opts) => {
    enterClockMode(reason, opts);
  }, []);
  const exit = useCallback<ClockMode["exit"]>((opts) => {
    exitClockMode(opts);
  }, []);
  const toggleDim = useCallback(() => {
    toggleClockDim(dim);
  }, [dim]);

  return { active: state.active, dim, state, enter, exit, toggleDim };
}
